import type { ScamTemplate, ScamType } from "./types";

export const SCAM_TEMPLATES: ScamTemplate[] = [
  {
    id: "FakeAirdrop",
    label: "Fake Airdrop Claim",
    shortLabel: "Airdrop",
    icon: "Gift",
    color: "#ff3d57",
    displayedIntent: "Claim 10,000 MON builder reward",
    calldataAction: "approve(spender, uint256.max)",
    hiddenPayload: "Unlimited token approval to an unknown spender",
    groundTruth: "scam",
    attackerAddress: "0xRUG00000000000000000000000000000000BEEF",
    fakeContract: "FakeAirdrop",
    riskFlags: ["unlimited approval", "intent mismatch", "unknown spender"]
  },
  {
    id: "ApprovalDrain",
    label: "Unlimited Approval Drain",
    shortLabel: "Approval",
    icon: "ShieldAlert",
    color: "#f8d66d",
    displayedIntent: "Verify wallet for Monad Blitz access",
    calldataAction: "setApprovalForAll(attacker, true)",
    hiddenPayload: "Collection-wide operator approval disguised as verification",
    groundTruth: "scam",
    attackerAddress: "0xA11CE0000000000000000000000000000000DRA1",
    fakeContract: "WalletVerifier",
    riskFlags: ["operator approval", "phishing verification", "asset drain path"]
  },
  {
    id: "FakeMint",
    label: "Fake NFT Mint",
    shortLabel: "Mint",
    icon: "Sparkles",
    color: "#16f2b3",
    displayedIntent: "Mint Monad Blitz Rare NFT",
    calldataAction: "transfer native fee to unknown treasury",
    hiddenPayload: "Mint page charges a fee but does not mint or assign an NFT",
    groundTruth: "scam",
    attackerAddress: "0xFEE000000000000000000000000000000000FA17",
    fakeContract: "RareMintPass",
    riskFlags: ["fee-only mint", "no token assignment", "unknown treasury"]
  },
  {
    id: "Honeypot",
    label: "Honeypot Token",
    shortLabel: "Honeypot",
    icon: "Radar",
    color: "#78a6ff",
    displayedIntent: "Buy early token before Monad listing",
    calldataAction: "buy() succeeds, sell() reverts for non-owner",
    hiddenPayload: "Token can be bought but normal users cannot exit",
    groundTruth: "scam",
    attackerAddress: "0x5E1100000000000000000000000000000000B10C",
    fakeContract: "EarlyMonadGem",
    riskFlags: ["sell blocked", "owner-only transfer", "liquidity trap"]
  },
  {
    id: "SafeClaim",
    label: "Legit Reward Claim",
    shortLabel: "Safe Claim",
    icon: "ShieldCheck",
    color: "#16f2b3",
    displayedIntent: "Claim verified Monad builder badge",
    calldataAction: "claim(address recipient) with no token approval",
    hiddenPayload: "No approval, no payment, recipient matches connected wallet",
    groundTruth: "safe",
    attackerAddress: "0x0000000000000000000000000000000000000000",
    fakeContract: "VerifiedBuilderBadge",
    riskFlags: ["verified contract", "no token movement", "recipient matches wallet"]
  },
  {
    id: "SafeSwap",
    label: "Normal Limited Swap",
    shortLabel: "Safe Swap",
    icon: "CheckCircle2",
    color: "#78a6ff",
    displayedIntent: "Swap 5 MON for a quoted token amount",
    calldataAction: "swapExactNativeForTokens with bounded slippage",
    hiddenPayload: "Expected router, bounded amount, no unlimited approval",
    groundTruth: "safe",
    attackerAddress: "0x0000000000000000000000000000000000000000",
    fakeContract: "MonadRouter",
    riskFlags: ["known router", "bounded slippage", "no unlimited approval"]
  }
];

export const RUG_STORM_TEMPLATES = SCAM_TEMPLATES.filter((item) => item.groundTruth === "scam");

export function templateByType(type: ScamType): ScamTemplate {
  return SCAM_TEMPLATES.find((item) => item.id === type) ?? SCAM_TEMPLATES[0];
}

export function deterministicTemplate(seed: number): ScamTemplate {
  return SCAM_TEMPLATES[Math.abs(seed) % SCAM_TEMPLATES.length];
}

export function deterministicRugTemplate(seed: number): ScamTemplate {
  return RUG_STORM_TEMPLATES[Math.abs(seed) % RUG_STORM_TEMPLATES.length];
}

export function ruleVerdict(template: ScamTemplate) {
  const scoreByType: Record<ScamType, number> = {
    FakeAirdrop: 96,
    ApprovalDrain: 94,
    FakeMint: 88,
    Honeypot: 91,
    SafeClaim: 8,
    SafeSwap: 18
  };
  const score = scoreByType[template.id];
  if (template.groundTruth === "safe") {
    return {
      verdict: "ALLOW",
      riskScore: score,
      attackVector: "none",
      reason: `${template.displayedIntent} matches the signed action: ${template.hiddenPayload.toLowerCase()}.`,
      safeAlternative: "Proceed if the displayed contract address and quoted parameters remain unchanged."
    } as const;
  }
  return {
    verdict: score >= 90 ? "BLOCK" : "WARN",
    riskScore: score,
    attackVector: template.riskFlags[0],
    reason: `${template.displayedIntent} hides ${template.hiddenPayload.toLowerCase()}.`,
    safeAlternative: "Do not sign. Inspect the actual action and only continue when the displayed intent matches calldata."
  } as const;
}
